import React, { useState, useEffect, useCallback } from 'react';
import {
  TrendingUp,
  Search,
  X,
  Download,
  ChevronUp,
  ChevronDown,
  Calendar,
  Package
} from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const CHART_COLORS = [
  { border: 'rgb(59, 130, 246)', bg: 'rgba(59, 130, 246, 0.1)' },   // blue
  { border: 'rgb(239, 68, 68)', bg: 'rgba(239, 68, 68, 0.1)' },     // red
  { border: 'rgb(34, 197, 94)', bg: 'rgba(34, 197, 94, 0.1)' },     // green
  { border: 'rgb(245, 158, 11)', bg: 'rgba(245, 158, 11, 0.1)' },   // amber
  { border: 'rgb(168, 85, 247)', bg: 'rgba(168, 85, 247, 0.1)' },   // purple
];

const ProductSalesTrendComponent = () => {
  // Product selection
  const [factoryProducts, setFactoryProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  // Year selection
  const currentYear = new Date().getFullYear();
  const [yearFrom, setYearFrom] = useState(currentYear);
  const [yearTo, setYearTo] = useState(currentYear);

  // Data
  const [trendData, setTrendData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);

  // Display mode
  const [displayMode, setDisplayMode] = useState('quantity'); // quantity | revenue
  const [sortConfig, setSortConfig] = useState({ key: 'year_month', direction: 'asc' });

  // Auto-search after top products are pre-selected
  const [autoSearchPending, setAutoSearchPending] = useState(false);

  // Load products on mount
  useEffect(() => {
    fetchFactoryProducts();
  }, []);

  const fetchFactoryProducts = async () => {
    setLoadingProducts(true);
    try {
      const response = await fetch('https://apisaleeasy.psisat.com/api/factory', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      if (result.status && result.data && result.data.length > 0) {
        setFactoryProducts(result.data);
        await fetchTopProducts(result.data);
      }
    } catch (err) {
      console.error('Error fetching factory products:', err);
    } finally {
      setLoadingProducts(false);
    }
  };

  const fetchTopProducts = async (products) => {
    try {
      const now = new Date();
      const dateFrom = `${currentYear}-01-01`;
      const dateTo = now.toISOString().slice(0, 10);
      const response = await fetch(
        `https://apisaleeasy.psisat.com/api/analytics/product-sales/top-products?dateFrom=${dateFrom}&dateTo=${dateTo}&limit=5`
      );
      if (!response.ok) return;
      const result = await response.json();
      if (result.success && result.data?.length > 0) {
        const matched = result.data
          .slice(0, 5)
          .map(tp => products.find(p => p.num_name_products === tp.product_code))
          .filter(Boolean);
        if (matched.length > 0) {
          setSelectedProducts(matched);
          setAutoSearchPending(true);
        }
      }
    } catch (err) {
      console.error('Error fetching top products:', err);
    }
  };

  // Auto-search when top products are pre-selected on mount
  useEffect(() => {
    if (autoSearchPending && selectedProducts.length > 0) {
      setAutoSearchPending(false);
      handleSearch();
    }
  }, [autoSearchPending, selectedProducts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter products for dropdown
  const filteredProducts = factoryProducts.filter(product => {
    if (!product.num_name_products) return false;
    // Exclude products with _1, _2, _3 suffix (internal data only)
    if (/_[1-3]/.test(product.num_name_products)) return false;
    // Exclude already selected
    if (selectedProducts.find(p => p.num_name_products === product.num_name_products)) return false;
    if (!productSearch.trim()) return true;
    const search = productSearch.toLowerCase();
    return product.num_name_products.toLowerCase().includes(search) ||
      (product.factory_name_products || '').toLowerCase().includes(search);
  });

  const displayProducts = productSearch.trim()
    ? filteredProducts.slice(0, 10)
    : filteredProducts.slice(0, 50);

  const handleProductSelect = (product) => {
    if (selectedProducts.length >= 5) return;
    setSelectedProducts(prev => [...prev, product]);
    setProductSearch('');
    setShowProductDropdown(false);
  };

  const handleRemoveProduct = (productCode) => {
    setSelectedProducts(prev => prev.filter(p => p.num_name_products !== productCode));
  };

  const handleSearch = async () => {
    if (selectedProducts.length === 0) {
      setError('กรุณาเลือกสินค้าอย่างน้อย 1 รายการ');
      return;
    }
    if (yearFrom > yearTo) {
      setError('ปีเริ่มต้นต้องไม่มากกว่าปีสิ้นสุด');
      return;
    }

    setLoading(true);
    setError('');
    setHasSearched(true);

    try {
      const productCodes = selectedProducts.map(p => p.num_name_products.trim());
      const response = await fetch('https://apisaleeasy.psisat.com/api/product-monthly-sales/compare', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productCodes,
          yearFrom,
          yearTo
        })
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();

      if (result.success) {
        setTrendData(result.data);
      } else {
        throw new Error(result.message || 'ไม่สามารถดึงข้อมูลได้');
      }
    } catch (err) {
      console.error('Error fetching trend data:', err);
      setError(err.message || 'เกิดข้อผิดพลาดในการดึงข้อมูล');
      setTrendData(null);
    } finally {
      setLoading(false);
    }
  };

  // Build color map based on selectedProducts order (stable mapping)
  const productColorMap = {};
  selectedProducts.forEach((p, idx) => {
    productColorMap[p.num_name_products.trim()] = CHART_COLORS[idx % CHART_COLORS.length];
  });

  // Chart data
  const getChartData = useCallback(() => {
    if (!trendData?.chart_ready) return null;
    const { months, datasets } = trendData.chart_ready;
    return {
      labels: months,
      datasets: datasets.map((ds) => {
        const color = productColorMap[ds.product_code] || CHART_COLORS[0];
        return {
          label: ds.label,
          data: displayMode === 'quantity' ? ds.data : ds.revenue_data,
          borderColor: color.border,
          backgroundColor: color.bg,
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          tension: 0.3,
          fill: false
        };
      })
    };
  }, [trendData, displayMode, productColorMap]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: { size: 12 }
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (context) => {
            const value = context.parsed.y;
            if (displayMode === 'revenue') {
              return `${context.dataset.label}: ฿${value.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            }
            return `${context.dataset.label}: ${value.toLocaleString('th-TH')} ชิ้น`;
          }
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 11 } }
      },
      y: {
        beginAtZero: true,
        ticks: {
          font: { size: 11 },
          callback: (value) => {
            if (displayMode === 'revenue') {
              return `฿${value.toLocaleString('th-TH')}`;
            }
            return value.toLocaleString('th-TH');
          }
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  // Table data
  const getTableData = () => {
    if (!trendData?.comparison_data) return [];
    let data = [...trendData.comparison_data];

    if (sortConfig.key === 'year_month') {
      data.sort((a, b) => {
        const cmp = a.year_month.localeCompare(b.year_month);
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      });
    }
    return data;
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return <ChevronUp className="w-3 h-3 text-gray-300" />;
    return sortConfig.direction === 'asc'
      ? <ChevronUp className="w-3 h-3 text-blue-600" />
      : <ChevronDown className="w-3 h-3 text-blue-600" />;
  };

  // Year options
  const yearOptions = [];
  for (let y = 2020; y <= currentYear + 1; y++) {
    yearOptions.push(y);
  }

  // Export Excel
  const handleExportExcel = () => {
    if (!trendData?.comparison_data || !trendData?.product_info) return;
    const productInfo = trendData.product_info;
    const rows = trendData.comparison_data.map(row => {
      const obj = { 'เดือน': row.month_display };
      productInfo.forEach(p => {
        const pd = row.products[p.product_code] || {};
        obj[`${p.product_code} - จำนวน`] = pd.quantity || 0;
        obj[`${p.product_code} - ยอดขาย`] = pd.revenue || 0;
        obj[`${p.product_code} - จำนวนบิล`] = pd.invoice_count || 0;
      });
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'แนวโน้มการขาย');
    const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buf], { type: 'application/octet-stream' }), `แนวโน้มการขายสินค้า_${yearFrom}-${yearTo}.xlsx`);
  };

  const chartData = getChartData();
  const tableData = getTableData();
  const productInfo = trendData?.product_info || [];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
            <TrendingUp className="w-6 h-6 text-orange-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">แนวโน้มการขายสินค้ารายเดือน</h1>
            <p className="text-gray-500 text-sm">เปรียบเทียบยอดขายสินค้าหลายชนิดตามช่วงเวลา</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
          {/* Product selection */}
          <div className="lg:col-span-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              เลือกสินค้า (สูงสุด 5 รายการ)
            </label>
            <div className="relative">
              <input
                type="text"
                value={productSearch}
                onChange={(e) => {
                  setProductSearch(e.target.value);
                  setShowProductDropdown(true);
                }}
                onFocus={() => setShowProductDropdown(true)}
                onBlur={() => setTimeout(() => setShowProductDropdown(false), 200)}
                placeholder={selectedProducts.length >= 5 ? 'เลือกได้สูงสุด 5 รายการ' : 'ค้นหารหัสหรือชื่อสินค้า...'}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                disabled={loadingProducts || selectedProducts.length >= 5}
              />
              <Search className="absolute right-3 top-2.5 w-4 h-4 text-gray-400" />

              {showProductDropdown && displayProducts.length > 0 && selectedProducts.length < 5 && (
                <div className="absolute z-20 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto mt-1">
                  {displayProducts.map((product) => (
                    <button
                      key={product.num_name_products}
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-orange-50 focus:bg-orange-50 border-b border-gray-100 last:border-b-0"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handleProductSelect(product)}
                    >
                      <div className="font-medium text-gray-900 text-sm">{product.num_name_products}</div>
                      {product.factory_name_products && (
                        <div className="text-xs text-gray-500 truncate">{product.factory_name_products}</div>
                      )}
                    </button>
                  ))}
                  {filteredProducts.length > displayProducts.length && (
                    <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50">
                      แสดง {displayProducts.length} จาก {filteredProducts.length} รายการ
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Selected products tags */}
            {selectedProducts.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedProducts.map((product, idx) => (
                  <span
                    key={product.num_name_products}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium"
                    style={{
                      backgroundColor: CHART_COLORS[idx % CHART_COLORS.length].bg,
                      color: CHART_COLORS[idx % CHART_COLORS.length].border,
                      border: `1px solid ${CHART_COLORS[idx % CHART_COLORS.length].border}`
                    }}
                  >
                    <Package className="w-3 h-3" />
                    {product.num_name_products}
                    <button
                      onClick={() => handleRemoveProduct(product.num_name_products)}
                      className="ml-0.5 hover:opacity-70"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Year From */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">ปีเริ่มต้น</label>
            <select
              value={yearFrom}
              onChange={(e) => setYearFrom(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              {yearOptions.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          {/* Year To */}
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">ปีสิ้นสุด</label>
            <select
              value={yearTo}
              onChange={(e) => setYearTo(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              {yearOptions.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          {/* Search Button */}
          <div className="lg:col-span-2 flex items-end">
            <button
              onClick={handleSearch}
              disabled={loading || selectedProducts.length === 0}
              className="w-full px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>กำลังค้นหา...</span>
                </>
              ) : (
                <>
                  <Search className="w-4 h-4" />
                  <span>ค้นหา</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {/* Results */}
      {hasSearched && !loading && trendData && (
        <>
          {/* Display mode toggle + Export */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">แสดงข้อมูล:</span>
              <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                <button
                  onClick={() => setDisplayMode('quantity')}
                  className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                    displayMode === 'quantity'
                      ? 'bg-orange-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  จำนวน (ชิ้น)
                </button>
                <button
                  onClick={() => setDisplayMode('revenue')}
                  className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                    displayMode === 'revenue'
                      ? 'bg-orange-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  ยอดขาย (บาท)
                </button>
              </div>
            </div>
            <button
              onClick={handleExportExcel}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
            >
              <Download className="w-4 h-4" />
              Export Excel
            </button>
          </div>

          {/* Chart */}
          {chartData && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {displayMode === 'quantity' ? 'กราฟแนวโน้มจำนวนสินค้า' : 'กราฟแนวโน้มยอดขาย'}
              </h3>
              <div style={{ height: '400px' }}>
                <Line data={chartData} options={chartOptions} />
              </div>
            </div>
          )}

          {/* Product Info Cards */}
          {productInfo.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {productInfo.map((p) => {
                const color = productColorMap[p.product_code] || CHART_COLORS[0];
                // Calculate totals
                const totalQty = trendData.comparison_data.reduce(
                  (sum, row) => sum + (row.products[p.product_code]?.quantity || 0), 0
                );
                const totalRev = trendData.comparison_data.reduce(
                  (sum, row) => sum + (row.products[p.product_code]?.revenue || 0), 0
                );
                const totalInv = trendData.comparison_data.reduce(
                  (sum, row) => sum + (row.products[p.product_code]?.invoice_count || 0), 0
                );
                return (
                  <div
                    key={p.product_code}
                    className="bg-white rounded-xl shadow-sm border-l-4 p-4"
                    style={{ borderLeftColor: color.border }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span
                        className="px-2 py-0.5 rounded text-xs font-bold text-white"
                        style={{ backgroundColor: color.border }}
                      >
                        {p.product_code}
                      </span>
                      <span className="text-sm text-gray-600 truncate">{p.product_description}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <div className="text-lg font-bold text-gray-900">{totalQty.toLocaleString('th-TH')}</div>
                        <div className="text-xs text-gray-500">จำนวนรวม</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-gray-900">฿{totalRev.toLocaleString('th-TH', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
                        <div className="text-xs text-gray-500">ยอดขายรวม</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-gray-900">{totalInv.toLocaleString('th-TH')}</div>
                        <div className="text-xs text-gray-500">จำนวนบิล</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Data Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">รายละเอียดข้อมูลรายเดือน</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      className="px-4 py-3 text-left font-medium text-gray-600 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('year_month')}
                    >
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        เดือน
                        <SortIcon columnKey="year_month" />
                      </div>
                    </th>
                    {productInfo.map((p) => (
                      <th
                        key={p.product_code}
                        colSpan={3}
                        className="px-2 py-3 text-center font-medium border-l border-gray-200"
                        style={{ color: (productColorMap[p.product_code] || CHART_COLORS[0]).border }}
                      >
                        {p.product_code} - {p.product_description}
                      </th>
                    ))}
                  </tr>
                  <tr className="bg-gray-100">
                    <th className="px-4 py-2"></th>
                    {productInfo.map((p) => (
                      <React.Fragment key={`sub-${p.product_code}`}>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-l border-gray-200">จำนวน</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-500">ยอดขาย</th>
                        <th className="px-2 py-2 text-center text-xs font-medium text-gray-500">บิล</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {tableData.map((row, rIdx) => (
                    <tr key={row.year_month} className={rIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3 font-medium text-gray-900 whitespace-nowrap">
                        {row.month_display}
                      </td>
                      {productInfo.map((p, pIdx) => {
                        const pd = row.products[p.product_code] || {};
                        return (
                          <React.Fragment key={`${row.year_month}-${p.product_code}`}>
                            <td className="px-2 py-3 text-right border-l border-gray-200">
                              {(pd.quantity || 0).toLocaleString('th-TH')}
                            </td>
                            <td className="px-2 py-3 text-right">
                              ฿{(pd.revenue || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-2 py-3 text-center">
                              {(pd.invoice_count || 0).toLocaleString('th-TH')}
                            </td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  ))}

                  {/* Totals row */}
                  {tableData.length > 0 && (
                    <tr className="bg-orange-50 font-semibold border-t-2 border-orange-200">
                      <td className="px-4 py-3 text-gray-900">รวมทั้งหมด</td>
                      {productInfo.map((p) => {
                        const totalQty = tableData.reduce((s, r) => s + (r.products[p.product_code]?.quantity || 0), 0);
                        const totalRev = tableData.reduce((s, r) => s + (r.products[p.product_code]?.revenue || 0), 0);
                        const totalInv = tableData.reduce((s, r) => s + (r.products[p.product_code]?.invoice_count || 0), 0);
                        return (
                          <React.Fragment key={`total-${p.product_code}`}>
                            <td className="px-2 py-3 text-right border-l border-gray-200">
                              {totalQty.toLocaleString('th-TH')}
                            </td>
                            <td className="px-2 py-3 text-right">
                              ฿{totalRev.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-2 py-3 text-center">
                              {totalInv.toLocaleString('th-TH')}
                            </td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Empty state */}
      {!hasSearched && !loading && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <TrendingUp className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-600 mb-2">เลือกสินค้าและช่วงปีเพื่อดูแนวโน้ม</h3>
          <p className="text-gray-400 text-sm">เลือกสินค้า 1-5 รายการ กำหนดปีเริ่มต้นและปีสิ้นสุด แล้วกดค้นหา</p>
        </div>
      )}

      {/* No data */}
      {hasSearched && !loading && !trendData && !error && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-600">ไม่พบข้อมูล</h3>
          <p className="text-gray-400 text-sm">ลองเปลี่ยนสินค้าหรือช่วงเวลา</p>
        </div>
      )}
    </div>
  );
};

export default ProductSalesTrendComponent;
